import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import { postAPI, postAPIForm } from '../../../app/api'
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg"

export default function NoticeManagement() {
    const [notices, setNotices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        notice_date: '',
        category_id: '',
        pdf_url: '',
        status: 1
    });

    // Fetch all notices and categories
    useEffect(() => {
        const initFetch = async () => {
            try {
                // Fetch categories
                const catResponse = await postAPI('notice-category/list', {});
                if (catResponse?.status === 1) {
                    const cats = catResponse?.data || [];
                    setCategories(cats);
                    // Set first category as default
                    if (cats.length > 0) {
                        setFormData(prev => ({ ...prev, category_id: cats[0].id }));
                    }
                }

                // Fetch notices
                setLoading(true);
                const noticeResponse = await postAPI('notice/list', {});
                if (noticeResponse?.status === 1) {
                    setNotices(noticeResponse?.data || []);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                Swal.fire('Error', 'Failed to fetch data', 'error');
            } finally {
                setLoading(false);
            }
        };
        initFetch();
    }, []);

    const fetchNotices = async () => {
        try {
            setLoading(true);
            const response = await postAPI('notice/list', {});
            if (response?.status === 1) {
                setNotices(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching notices:', error);
            Swal.fire('Error', 'Failed to fetch notices', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            notice_date: '',
            category_id: categories.length > 0 ? categories[0].id : '',
            pdf_url: '',
            status: 1
        });
        setShowForm(false);
        setEditingId(null);
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (files) {
            // Handle file input
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
            }));
        }
    };

    // Add/Update notice
    const handleAddNotice = async (e) => {
        e.preventDefault();
        try {
            if (!formData.title || !formData.description || !formData.notice_date) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            let submitData = {
                title: formData.title,
                description: formData.description,
                notice_date: formData.notice_date,
                category_id: formData.category_id,
                status: formData.status
            };

            // If PDF file exists and is a File object, upload it
            if (formData.pdf_file && formData.pdf_file instanceof File) {
                const fileFormData = new FormData();
                fileFormData.append('baseDir', 'notices');
                fileFormData.append('filename', 'notice_pdf');
                fileFormData.append('original_file', formData.pdf_file);

                const uploadResponse = await postAPIForm('file/uploadFile', fileFormData);
                if (uploadResponse?.status === 1) {
                    submitData.pdf_url = uploadResponse?.filepath;
                } else {
                    Swal.fire('Error', 'Failed to upload PDF file', 'error');
                    return;
                }
            } else if (formData.pdf_url && typeof formData.pdf_url === 'string') {
                // Keep existing PDF URL if not uploading a new file
                submitData.pdf_url = formData.pdf_url;
            }

            if (editingId) submitData.id = editingId;

            const response = await postAPI('notice/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Notice saved successfully', 'success');
                resetForm();
                fetchNotices();
                // Close modal
                document.querySelector('[data-bs-dismiss="modal"]')?.click();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save notice', 'error');
            }
        } catch (error) {
            console.error('Error saving notice:', error);
            Swal.fire('Error', error.message || 'Failed to save notice', 'error');
        }
    };

    // Delete notice
    const handleDeleteNotice = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this notice?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('notice/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Notice deleted successfully', 'success');
                    fetchNotices();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete notice', 'error');
            }
        }
    };

    // Toggle notice visibility (show/hide)
    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 1 ? 0 : 1;
        const actionText = newStatus === 1 ? 'show' : 'hide';

        try {
            const response = await postAPI('notice/create', {
                id: item.id,
                title: item.title,
                description: item.description,
                notice_date: item.notice_date,
                category_id: item.category_id,
                pdf_url: item.pdf_url,
                status: newStatus
            });

            if (response?.status === 1) {
                Swal.fire('Success', `Notice ${actionText === 'show' ? 'is now visible' : 'is now hidden'}`, 'success');
                fetchNotices();
            } else {
                Swal.fire('Error', response?.msg || `Failed to ${actionText} notice`, 'error');
            }
        } catch (error) {
            console.error('Error toggling notice status:', error);
            Swal.fire('Error', `Failed to ${actionText} notice`, 'error');
        }
    };

    const handleToggleForm = () => {
        if (showForm) {
            setShowForm(false);
            resetForm();
        } else {
            setEditingId(null);
            setFormData({
                title: '',
                description: '',
                notice_date: '',
                category_id: categories.length > 0 ? categories[0].id : '',
                status: 1
            });
            setShowForm(true);
        }
    };

    const handleEditNotice = (item) => {
        setFormData({
            title: item.title || '',
            description: item.description || '',
            notice_date: item.notice_date ? item.notice_date.split('T')[0] : '',
            category_id: item.category_id || (categories.length > 0 ? categories[0].id : ''),
            pdf_url: item.pdf_url || '',
            status: item.status || 1
        });
        setEditingId(item.id);
        setShowForm(true);
    };


    return (
        <>
            <div className="container-fluid">
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <h3 className="mb-0">Notice Management</h3>
                            {/* <button 
                                className="btn btn-primary"
                                onClick={handleToggleForm}
                            >
                                {showForm ? 'Cancel' : 'Add New Notice'}
                            </button> */}
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    resetForm();
                                    document.getElementById("openNoticeModalBtn").click();
                                }}
                            >
                                {showForm ? 'Cancel' : 'Add New Notice'}
                            </button>
                        </div>
                    </div>
                </div>
                <button
                    id="openNoticeModalBtn"
                    className="d-none"
                    type="button"
                    data-bs-toggle="modal"
                    data-bs-target="#noticeModal"
                ></button>
                {/* {showForm && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5 className="card-title">{editingId ? 'Edit Notice' : 'Add New Notice'}</h5>
                            <form onSubmit={handleAddNotice}>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            placeholder="e.g., Foundation Day Celebration"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Date *</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="notice_date"
                                            value={formData.notice_date}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Category *</label>
                                        <select
                                            className="form-control"
                                            name="category_id"
                                            value={formData.category_id}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select a category</option>
                                            {categories && categories.length > 0 ? (
                                                categories.map((cat, index) => (
                                                    <option key={index} value={cat.id}>
                                                        {cat.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <option value="">No categories available</option>
                                            )}
                                        </select>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <label className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            rows="4"
                                            placeholder="Enter notice description"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-control"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >
                                            <option value={1}>Active</option>
                                            <option value={0}>Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-success">
                                    {editingId ? 'Update' : 'Add'} Notice
                                </button>
                            </form>
                        </div>
                    </div>
                )} */}
                <div className="modal fade modal-white" id="noticeModal" tabIndex="-1" aria-hidden="true">
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingId ? "Edit Notice" : "Add New Notice"}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                ></button>
                            </div>

                            <div className="modal-body">
                                <form onSubmit={handleAddNotice}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                placeholder="e.g., Foundation Day Celebration"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Date <small className="text-muted">(Optional)</small></label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="notice_date"
                                                value={formData.notice_date}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Category *</label>
                                            <select
                                                className="form-control"
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select a category</option>
                                                {categories && categories.length > 0 ? (
                                                    categories.map((cat, index) => (
                                                        <option key={index} value={cat.id}>
                                                            {cat.name}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option value="">No categories available</option>
                                                )}
                                            </select>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Description *</label>
                                            <textarea
                                                className="form-control"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="4"
                                                placeholder="Enter notice description"
                                                required
                                            ></textarea>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">PDF Document (Optional)</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                name="pdf_file"
                                                onChange={handleChange}
                                                accept=".pdf,application/pdf"
                                            />
                                            {formData.pdf_url && typeof formData.pdf_url === 'string' && (
                                                <small className="text-muted mt-1 d-block">
                                                    Current PDF: <a href={formData.pdf_url} target="_blank" rel="noopener noreferrer">View PDF</a>
                                                </small>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button type="submit" className="btn btn-success">
                                        {editingId ? 'Update' : 'Add'} Notice
                                    </button>
                                </form>
                            </div>

                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover cus-table">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Title</th>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notices && notices.length > 0 ? (
                                            notices.map((item, index) => (
                                                <tr key={index}>
                                                    <td><strong>{item.title}</strong></td>
                                                    <td>{new Date(item.notice_date).toLocaleDateString()}</td>
                                                    <td>
                                                        <span className="badge badge-primary">
                                                            {categories.find(c => c.id === item.category_id)?.name || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>{item.description?.substring(0, 40)}...</td>
                                                    <td>
                                                        <span className={`badge badge-${item.status === 1 ? 'success' : 'danger'}`}>
                                                            {item.status === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`btn btn-sm me-2 ${item.status === 1 ? 'btn-warning' : 'btn-success'}`}
                                                            onClick={() => handleToggleStatus(item)}
                                                            title={item.status === 1 ? 'Hide this notice' : 'Show this notice'}
                                                        >
                                                            {item.status === 1 ? 'Hide' : 'Show'}
                                                        </button>
                                                        <button
                                                            className="btn py-0 my-0 mx-0"
                                                            onClick={() => handleDeleteNotice(item.id)}
                                                        >
                                                            <img src={deleteIcon} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    No notices found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
